import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import ReadElementProps from '../components/ReadElementProps.js';
import PageMeta from '../components/PageMeta';
import GoToHomePage from '../components/GoToHomePage.js';
import i18next from 'i18next';
import { changeCategoryColor, getCategoryName } from '../services/functions';
import { elementsTheme } from '../constants/elementsTheme';
import { useEffect, useRef, useState } from 'react';
import { elementCategories } from '../constants/elementCategories';
import { useAuth } from '../hooks/useAuth';
import Loader from '../components/Loader';
import { useRecoilState } from 'recoil';
import { qrModalState } from '../states/atoms';
import { FirebaseBrain } from '../services/FirebaseBrain/FirebaseBrain';
import { FirebaseQrSource, FirebaseBrainScreens } from '../services/FirebaseBrain/FirebaseBrainScreens';

//Elements
const ReadElementHeader = styled.div`
    height: 300px;
    position: relative;
    padding-bottom: 20px;
    border-bottom: 2px solid;
    border-color: ${props => props.borderColor};

    @media (min-width: 1600px) {
        height: 380px;
    }

    @media (min-width: 1900px) {
        height: 420px;
    }

    &::after {
        content: '';
        width: 28px;
        height: 28px;
        position: absolute;
        z-index: 1;
        left: 50%;
        margin-left: -14px;
        bottom: -30px;
        border: 14px solid transparent;
        border-top-color: ${props => props.borderColor};
    }
`;

const ReadElementImage = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.backgroundColor};
    z-index: -1;
`;

const Separator = styled.span`
    display: inline-block;
    width: 4px;
    height: 4px;
    background-color: ${props => props.bgColor};
    border-radius: 50%;
`;

const CategoryCard = styled.div`
    font-weight: 700;
    font-family: 'OpenSans', 'Arial', sans-serif;
    display: inline-block;
    background-color: ${props => props.backgroundColor};
    color: ${props => props.textColor};
    padding: 12px 20px;
    border-radius: 4px 0 0 4px;
    font-size: 12px;
    text-transform: uppercase;
`;

const SmallHeaderSymbol = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'OpenSans', 'Arial', sans-serif;
    font-weight: 700;
    font-size: 17px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: ${props => props.backgroundColor};
    color: ${props => props.textColor};
    margin-right: 19px;
    margin-left: 10px;
`;

const SmallHeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 10px;
    border-bottom: 2px solid;
    border-color: ${props => props.borderColor};
    position: relative;

    &::after {
        content: '';
        width: 28px;
        height: 28px;
        position: absolute;
        z-index: 1;
        left: 50%;
        margin-left: -14px;
        bottom: -30px;
        border: 14px solid transparent;
        border-top-color: ${props => props.borderColor};
    }
`;

const ReadElementPage = ({ id }) => {
  const itemId = Number(id);
  const { t } = useTranslation();
  const { getAuthCookie, isAuthenticated } = useAuth();
  const history = useHistory();
  const [, setQrModal] = useRecoilState(qrModalState);
  const [showHeader, setShowHeader] = useState(false);
  const headerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState();

  const api = `${process.env.REACT_APP_API_HOST}/periodic/element/${itemId}`;

  // isCurrent() — false, если пользователь уже перешёл к другому элементу: устаревший ответ не должен перезаписать данные
  const getElementData = async (isCurrent) => {
    try {
      const headers = {
        'Content-Type': 'application/json'
      };

      const token = getAuthCookie();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(api, {
        method: 'GET',
        headers
      })
        .then(data => data.json())
        .then(data => {
          if (isCurrent()) {
            setData(data);
          }
        });
    } catch (error) {
      console.error(error);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  };

  const showQrModal = () => {
    if (!isAuthenticated) {
      FirebaseBrain.qrModalOpen(FirebaseQrSource.READ_COMPARE, itemId);
      history.push('/');
      setQrModal(true);
    }
  };

  useEffect(() => {
    let current = true;
    getElementData(() => current);
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  useEffect(() => {
    if (!isLoading) {
      const container = document.querySelector('.read-element__container');
      const handleScroll = () => {
        if (container.scrollTop > (headerRef.current.clientHeight + 60)) {
          setShowHeader(true);
        } else {
          setShowHeader(false);
        }
      };

      container.addEventListener('scroll', handleScroll);

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isLoading]);

  const setElementTheme = () => {
    if (elementsTheme[itemId - 1] === true) {
      return 'read-element__header--white';
    } else if (elementsTheme[itemId - 1] === false) {
      return 'read-element__header--dark';
    }
  };

  const changeWhiteColor = (category) => {
    switch (category) {
      case 'L':
        return '#000000';
      default:
        return '#FFFFFF';
    }
  };

  // Описание для поисковиков: «Азот (N). Атомная масса: 14.007 (г/моль); Плотность: …»
  const getMetaDescription = () => {
    const properties = data?.PropertiesCommon;
    if (!properties) {
      return null;
    }
    const parts = [];
    if (properties.elementMasse) {
      const mass = Number(properties.elementMasse);
      const massValue = Number.isNaN(mass) ? properties.elementMasse : Number(mass.toFixed(3));
      parts.push(`${t('string.translate.read_atom_massa')} ${massValue} ${t('string.translate.read_gramm_moll')}`);
    }
    if (properties.elementDensity) {
      parts.push(`${t('string.translate.read_density')} ${properties.elementDensity} ${t('string.translate.read_gramm_santim')}`);
    }
    [
      ['read_temp1', properties.elementMeltingPoint],
      ['read_temp2', properties.elementBoilingPoint]
    ].forEach(([label, value]) => {
      if (value !== null && value !== undefined && value !== '**') {
        parts.push(`${t(`string.translate.${label}`)} ${value} °C`);
      }
    });
    const head = `${t(`table.element${itemId}.name`)} (${t(`table.element${itemId}.symbol`)}).`;
    return parts.length ? `${head} ${parts.join('; ')}.` : head;
  };

  return (<>
    <PageMeta
      title={`${t(`table.element${itemId}.name`)} (${t(`table.element${itemId}.symbol`)}) – ${t('string.translate.read_properties').replace(/[:：]\s*$/, '')} | ${t('string.translate.name_ab')}`}
      description={getMetaDescription()}
    />
    <GoToHomePage classNames={'go-to-homepage__full-size'} />
    {/* data-element — номер элемента, данные которого уже загружены (по нему пререндер понимает, что страница готова) */}
    <section className='read-element' data-element={data?.number}>
      {!isLoading ? (<>
        <div className={`read-element__small-header ${showHeader ? 'visible' : 'hidden'}`}>
          <SmallHeaderContainer borderColor={changeCategoryColor(elementCategories[itemId - 1])}>
            <div className='read-element__small-header-wrapper'>
              <Link className='read-element__go-back-btn' to='/' />
              <SmallHeaderSymbol backgroundColor={() => changeCategoryColor(elementCategories[itemId - 1])}
                                 textColor={() => changeWhiteColor(elementCategories[itemId - 1])}>
                {data.symbol}
              </SmallHeaderSymbol>
              <div className='read-element__small-info-wrapper'>
                <div className='read-element__small-element-name'>
                  {t(`table.element${itemId}.name`)}
                </div>
                <div className='read-element__small-element-weight'>
                  {data.PropertiesCommon.elementMasse}
                  <span> {t(`string.translate.read_gramm_moll`)}</span>
                </div>
              </div>
            </div>
            {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
            <a
              className='read-element__small-wiki-link'
              href={`https://${i18next.language}.wikipedia.org/wiki/${t(`table.element${itemId}.name`)}`}
              aria-label='Wiki link'
              target='_blank'
              rel='noreferrer'
              onClick={() => FirebaseBrain.readElementClicks(FirebaseBrainScreens.READ_ELEMENT_INFO_WIKI)}
            />
          </SmallHeaderContainer>
          <div className='read-element__buttons'>
            {itemId <= 1 ? (<div className='read-element__element-link--prev-plug' />) : (
              <Link
                to={`/element/${itemId - 1}`}
                className='read-element__element-link read-element__element-link--prev'
                onClick={() => FirebaseBrain.readElementNavigation(itemId - 1, FirebaseBrainScreens.READ_NAVIGATION_PREW)}
              >
              <span className='read-element__button-element-number'>
                {itemId - 1}
              </span>
              <Separator bgColor={changeCategoryColor(elementCategories[itemId - 2])} />
              <span className='read-element__button-element-name'>
                {t(`table.element${itemId - 1}.name`)}
              </span>
            </Link>)}
            {itemId >= 127 ? null : (
              <Link
                to={`/element/${itemId + 1}`}
                className='read-element__element-link read-element__element-link--next'
                onClick={() => FirebaseBrain.readElementNavigation(itemId - 1, FirebaseBrainScreens.READ_NAVIGATION_NEXT)}
              >
              <span className='read-element__button-element-number'>
                {itemId + 1}
              </span>
              <Separator bgColor={changeCategoryColor(elementCategories[itemId])} />
              <span className='read-element__button-element-name'>
                {t(`table.element${itemId + 1}.name`)}
              </span>
            </Link>)}
          </div>
        </div>
        <div className='read-element__container'>
          <ReadElementHeader borderColor={changeCategoryColor(elementCategories[itemId - 1])} ref={headerRef}>
            <div className='read-element__gradient' />
            <ReadElementImage
              backgroundColor={changeCategoryColor(elementCategories[itemId - 1])}
            />
            <div className='read-element__content'>
              <div className={`read-element__header ${setElementTheme()}`}>
                <Link className='read-element__go-back-btn' to='/' />
                <div className='read-element__buttons-wrapper'>
                  {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
                  <a
                    className='read-element__wiki'
                    href={`https://${i18next.language}.wikipedia.org/wiki/${t(`table.element${itemId}.name`)}`}
                    aria-label='Wiki link'
                    target='_blank'
                    rel='noreferrer'
                    onClick={() => FirebaseBrain.readElementClicks(FirebaseBrainScreens.READ_ELEMENT_INFO_WIKI)}
                  />
                  {isAuthenticated ? <Link className='read-element__compare-btn' to={`/element/${itemId}/compare`} /> :
                    <button className='read-element__compare-btn read-element__compare-btn--locked' onClick={() => !isAuthenticated && showQrModal()}/>
                  }
                </div>
              </div>
              <div className='read-element__info-container'>
                <div className='read-element__selected-element'>
                <span className='read-element__name'>
                  {t(`table.element${itemId}.name`)}
                </span>
                  <div className='read-element__symbol-wrapper'>
                  <span className='read-element__symbol'>
                    {data.symbol}
                  </span>
                    <span className='read-element__number'>
                    {itemId}
                  </span>
                  </div>
                  <span className='read-element__weight'>
                  {data.PropertiesCommon.elementMasse}
                    <span> {t(`string.translate.read_gramm_moll`)}</span>
                </span>
                </div>
                <div className='read-element__groups'>
                  {data.OverviewCommon.elementRadioactivity && (<span className='read-element__radioactive'>
                    {t(`string.translate.ys1`).replace(':', '')}
                  </span>)}
                  <CategoryCard
                    backgroundColor={changeCategoryColor(elementCategories[itemId - 1])}
                    textColor={changeWhiteColor(elementCategories[itemId - 1])}
                  >
                  <span className='read-element__category'>
                    {getCategoryName(itemId)}
                  </span>
                  </CategoryCard>
                </div>
              </div>
            </div>
            {(data && data.number === itemId && data.OverviewCommon.hasImage) && (<picture>
              <source
                type='image/avif'
                srcSet={`${require(`../assets/images/elements-avif/element_${itemId}.avif`)} 1x,
        				${require(`../assets/images/elements-2x-avif/element_${itemId}.avif`)} 2x
        				`}
              />
              <source
                type='image/webp'
                srcSet={`${require(`../assets/images/elements-webp/element_${itemId}.webp`)} 1x,
        				${require(`../assets/images/elements-2x-webp/element_${itemId}.webp`)} 2x
        				`}
              />
              <img
                className='read-element__element-image'
                src={require(`../assets/images/elements-jpg/element_${itemId}.jpg`)}
                srcSet={`${require(`../assets/images/elements-2x-jpg/element_${itemId}.jpg`)} 2x`}
                alt=''
              />
            </picture>)}
          </ReadElementHeader>
          <div className='read-element__buttons'>
            {itemId <= 1 ? (<div className='read-element__element-link--prev-plug' />) : (
              <Link
                to={`/element/${itemId - 1}`}
                className='read-element__element-link read-element__element-link--prev'
                onClick={() => FirebaseBrain.readElementNavigation(itemId - 1, FirebaseBrainScreens.READ_NAVIGATION_PREW)}
              >
              <span className='read-element__button-element-number'>
                {itemId - 1}
              </span>
              <Separator bgColor={changeCategoryColor(elementCategories[itemId - 2])} />
              <span className='read-element__button-element-name'>
                {t(`table.element${itemId - 1}.name`)}
              </span>
            </Link>)}
            {itemId >= 127 ? null : (
              <Link
                to={`/element/${itemId + 1}`}
                className='read-element__element-link read-element__element-link--next'
                onClick={() => FirebaseBrain.readElementNavigation(itemId - 1, FirebaseBrainScreens.READ_NAVIGATION_NEXT)}
              >
              <span className='read-element__button-element-number'>
                {itemId + 1}
              </span>
              <Separator bgColor={changeCategoryColor(elementCategories[itemId])} />
              <span className='read-element__button-element-name'>
                {t(`table.element${itemId + 1}.name`)}
              </span>
            </Link>)}
          </div>
          <ReadElementProps data={data} itemId={itemId} />
        </div>
      </>) : <div className='read-element__loader-wrapper'>
        <Loader itemId={itemId} size='small' />
      </div>}
    </section>
  </>);
};

export default ReadElementPage;